import React, { useState, useRef } from 'react';
import {
  X, Upload, FileDown, FileSpreadsheet, CheckCircle2, AlertTriangle,
  Loader2, ArrowRight, RotateCcw, Car, Tag,
} from 'lucide-react';
import { parseCSV } from '../../lib/csv';
import { carAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Rental and Sale vehicles need different columns, so each has its own template.
const COMMON = ['registrationNumber', 'make', 'model', 'year', 'vehicleType', 'fuelType', 'transmission', 'seats', 'color', 'mileage'];
const LOCATION = ['city', 'state', 'description', 'latitude', 'longitude', 'imageUrls'];

const TEMPLATES = {
  rental: {
    // rental pricing fields
    headers: [...COMMON, 'pricePerDay', 'pricePerHour', 'pricePerKm', 'minBookingDays', ...LOCATION],
    sample: [
      ['KA01AB1234', 'Maruti', 'Swift', '2021', 'car', 'Petrol', 'Manual', '5', 'White', '45000', '1800', '150', '12', '1', 'Bangalore', 'Karnataka', 'Well maintained', '12.9716', '77.5946'],
      ['KA03EF3456', 'Toyota', 'Innova', '2022', 'suv', 'Diesel', 'Manual', '7', 'Grey', '30000', '3500', '250', '18', '2', 'Bangalore', 'Karnataka', 'Outstation ready', '', ''],
    ],
    fileName: 'rental-vehicles-template.csv',
  },
  sale: {
    // sale price + resale detail fields
    headers: [...COMMON, 'salePrice', 'salePriceType', 'kmDriven', 'noOfOwners', 'condition', 'insuranceValidTill', 'registrationYear', 'accidentFree', 'rcAvailable', ...LOCATION],
    sample: [
      ['KA05XY9090', 'Hyundai', 'i20', '2018', 'car', 'Petrol', 'Manual', '5', 'Red', '62000', '450000', 'negotiable', '62000', '1', 'good', '2025-12-31', '2018', 'yes', 'yes', 'Bangalore', 'Karnataka', 'Single owner, insured', '12.9716', '77.5946'],
      ['KA07PQ1212', 'Mahindra', 'XUV500', '2017', 'suv', 'Diesel', 'Manual', '7', 'Black', '88000', '720000', 'fixed', '88000', '2', 'fair', '2025-06-30', '2017', 'yes', 'yes', 'Mysore', 'Karnataka', 'Well serviced', '', ''],
    ],
    fileName: 'sale-vehicles-template.csv',
  },
};

const FUELS = ['petrol', 'diesel', 'electric', 'ev', 'hybrid', 'cng'];
const TRANS = ['manual', 'mt', 'automatic', 'auto', 'at'];
const CONDITIONS = ['excellent', 'good', 'fair', 'needs-work'];

// Same rule as the backend: ignore case/spaces/hyphens when comparing plates.
const canonReg = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const csvCell = (v) => {
  const s = String(v ?? '');
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function buildTemplate(mode) {
  const t = TEMPLATES[mode];
  const lines = [t.headers.join(',')];
  t.sample.forEach((r) => lines.push(r.map(csvCell).join(',')));
  return lines.join('\r\n');
}

// Client-side validation mirrors the backend so issues show before uploading.
function validateRow(r, mode) {
  const missing = [];
  if (!String(r.registrationNumber || '').trim()) missing.push('registrationNumber');
  if (!String(r.make || '').trim()) missing.push('make');
  if (!String(r.model || '').trim()) missing.push('model');
  if (missing.length) return { ok: false, message: `Missing: ${missing.join(', ')}` };

  const fuel = String(r.fuelType || '').trim().toLowerCase();
  if (fuel && !FUELS.includes(fuel)) return { ok: false, message: `Bad fuelType "${r.fuelType}"` };
  const trans = String(r.transmission || '').trim().toLowerCase();
  if (trans && !TRANS.includes(trans)) return { ok: false, message: `Bad transmission "${r.transmission}"` };

  if (mode === 'sale') {
    const salePrice = Number(String(r.salePrice || '').replace(/[₹,\s]/g, ''));
    if (!(salePrice > 0)) return { ok: false, message: 'salePrice is required for sale vehicles' };
    const cond = String(r.condition || '').trim().toLowerCase();
    if (cond && !CONDITIONS.includes(cond)) return { ok: false, message: `Bad condition "${r.condition}" (excellent/good/fair/needs-work)` };
  }
  return { ok: true, message: 'Ready' };
}

const StatusPill = ({ status }) => {
  const map = {
    created: 'bg-emerald-50 text-emerald-700',
    ready: 'bg-emerald-50 text-emerald-700',
    updated: 'bg-blue-50 text-blue-700',
    skipped: 'bg-amber-50 text-amber-700',
    error: 'bg-red-50 text-red-700',
  };
  const label = { created: 'Added', ready: 'Ready', updated: 'Updated', skipped: 'Skipped', error: 'Error' }[status] || status;
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${map[status] || 'bg-gray-100 text-gray-600'}`}>{label}</span>;
};

export default function BulkImportModal({ onClose, onImported }) {
  const { user } = useAuth();
  const businessType = user?.businessType || 'both';
  const [mode, setMode] = useState(businessType === 'sale' ? 'sale' : 'rental'); // rental | sale
  const [stage, setStage] = useState('pick'); // pick | preview | done
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState([]); // [{ ...row, _valid, _message }]
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const validRows = parsed.filter((r) => r._valid);
  const badRows = parsed.filter((r) => !r._valid);

  const downloadTemplate = () => {
    const t = TEMPLATES[mode];
    const blob = new Blob(['﻿' + buildTemplate(mode)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = t.fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Validate + flag duplicates, then move to preview.
  const processRows = (rows) => {
    if (!rows.length) {
      setParseError('No rows found. Make sure the file has a header row and at least one vehicle.');
      setParsed([]);
      return;
    }
    const seen = new Set();
    const marked = rows.map((r) => {
      const v = validateRow(r, mode);
      const reg = canonReg(r.registrationNumber);
      let { ok, message } = v;
      if (ok && reg) {
        if (seen.has(reg)) { ok = false; message = 'Duplicate registration in file'; }
        else seen.add(reg);
      }
      return { ...r, _valid: ok, _message: message };
    });
    setParsed(marked);
    setStage('preview');
  };

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        if (isExcel) {
          // Lazy-load SheetJS only when an Excel file is chosen
          const XLSX = await import('xlsx');
          const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          // header row -> object keys; empty cells become '' and numbers -> strings
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false })
            .map((row) => {
              const obj = {};
              Object.keys(row).forEach((k) => { obj[String(k).trim()] = row[k]; });
              return obj;
            });
          processRows(rows);
        } else {
          const { rows } = parseCSV(String(e.target.result || ''));
          processRows(rows);
        }
      } catch (err) {
        setParseError('Could not read this file. Please upload a valid CSV or Excel (.xlsx) file.');
        setParsed([]);
      }
    };

    if (isExcel) reader.readAsArrayBuffer(file);
    else reader.readAsText(file);
  };

  const doImport = async () => {
    if (!validRows.length) return;
    setImporting(true);
    try {
      // Tag every row with the chosen purpose so the backend sets rent/sale flags.
      const flags = mode === 'sale' ? { forSale: 'yes', forRent: 'no' } : { forRent: 'yes', forSale: 'no' };
      const payload = validRows.map(({ _valid, _message, ...row }) => ({ ...row, ...flags }));
      const res = await carAPI.bulkImport(payload);
      setResult(res.data);
      setStage('done');
      onImported?.(res.data);
    } catch (err) {
      setParseError(err.response?.data?.message || 'Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setStage('pick'); setParsed([]); setFileName(''); setResult(null); setParseError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><FileSpreadsheet className="w-5 h-5" /></span>
            <div>
              <h3 className="font-bold text-gray-900">Import vehicles</h3>
              <p className="text-xs text-gray-500">Bulk-add from a CSV or Excel file</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 overflow-y-auto">
          {/* STAGE: pick file */}
          {stage === 'pick' && (
            <>
              {/* Rental vs Sale — they need different columns, so pick one */}
              {businessType === 'both' ? (
                <div className="mb-5">
                  <p className="text-sm font-semibold text-gray-800 mb-2">What are you importing?</p>
                  <div className="grid grid-cols-2 gap-2.5">
                    <button onClick={() => setMode('rental')}
                      className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm border-2 transition-colors ${mode === 'rental' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                      <Car className="w-4 h-4" /> Rental vehicles
                    </button>
                    <button onClick={() => setMode('sale')}
                      className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm border-2 transition-colors ${mode === 'sale' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                      <Tag className="w-4 h-4" /> Vehicles for sale
                    </button>
                  </div>
                </div>
              ) : (
                <div className={`mb-5 inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-full ${mode === 'sale' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                  {mode === 'sale' ? <><Tag className="w-4 h-4" /> Importing vehicles for sale</> : <><Car className="w-4 h-4" /> Importing rental vehicles</>}
                </div>
              )}

              <div className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-xl p-4 mb-5">
                <div className="text-sm text-gray-700 leading-relaxed">
                  <p className="font-semibold text-gray-900 mb-1">How it works</p>
                  <p>1. Download the <span className="font-semibold">{mode === 'sale' ? 'Sale' : 'Rental'} template</span> (or <span className="font-semibold">Export CSV</span> to edit existing) &nbsp;•&nbsp; 2. Fill one vehicle per row &nbsp;•&nbsp; 3. Upload it back.</p>
                  <p className="mt-1.5 text-xs text-gray-500"><span className="font-semibold">Required:</span> registrationNumber, make, model{mode === 'sale' ? ', salePrice' : ''}. A row whose registration already exists <span className="font-semibold">updates</span> that vehicle; new ones are added.</p>
                  <p className="mt-1 text-xs text-gray-500"><span className="font-semibold">Map location:</span> add <span className="font-mono">latitude</span>/<span className="font-mono">longitude</span> for an exact pin (in Google Maps, right-click the spot → click the coordinates to copy). Leave them blank to use your saved business location.</p>
                  <p className="mt-1 text-xs text-gray-500"><span className="font-semibold">Photos:</span> put image links in <span className="font-mono">imageUrls</span> separated by <span className="font-mono">|</span>, or add photos per vehicle from its page after importing.</p>
                </div>
              </div>

              <button onClick={downloadTemplate}
                className={`w-full flex items-center justify-center gap-2 py-2.5 mb-4 rounded-xl font-semibold transition-colors ${mode === 'sale' ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100' : 'text-blue-700 bg-blue-50 hover:bg-blue-100'}`}>
                <FileDown className="w-4 h-4" /> Download {mode === 'sale' ? 'Sale' : 'Rental'} template (CSV)
              </button>

              <label className="block cursor-pointer">
                <input ref={fileRef} type="file" accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])} />
                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center hover:border-blue-300 hover:bg-blue-50/40 transition-colors">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="font-semibold text-gray-800">Choose a CSV or Excel file</p>
                  <p className="text-xs text-gray-500 mt-1">.csv, .xlsx or .xls — or drag it onto this box</p>
                </div>
              </label>
              {parseError && <p className="text-sm text-red-600 mt-3 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {parseError}</p>}
            </>
          )}

          {/* STAGE: preview */}
          {stage === 'preview' && (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-sm font-medium text-gray-600 flex items-center gap-1.5"><FileSpreadsheet className="w-4 h-4" /> {fileName}</span>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">{validRows.length} ready</span>
                {badRows.length > 0 && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-700">{badRows.length} with issues</span>}
                <button onClick={reset} className="ml-auto text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Change file</button>
              </div>

              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-left text-xs text-gray-500">
                        <th className="px-3 py-2 font-semibold">#</th>
                        <th className="px-3 py-2 font-semibold">Reg. No</th>
                        <th className="px-3 py-2 font-semibold">Vehicle</th>
                        <th className="px-3 py-2 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {parsed.map((r, i) => (
                        <tr key={i} className={r._valid ? '' : 'bg-red-50/40'}>
                          <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                          <td className="px-3 py-2 font-mono text-xs font-semibold text-gray-800">{String(r.registrationNumber || '—').toUpperCase()}</td>
                          <td className="px-3 py-2 text-gray-600">{[r.make, r.model].filter(Boolean).join(' ') || '—'}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <StatusPill status={r._valid ? 'ready' : 'error'} />
                              {!r._valid && <span className="text-xs text-red-600">{r._message}</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {badRows.length > 0 && (
                <p className="text-xs text-amber-600 mt-3 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Rows with issues will be skipped. Fix them in the CSV and re-upload to include them.
                </p>
              )}
              {parseError && <p className="text-sm text-red-600 mt-3 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {parseError}</p>}
            </>
          )}

          {/* STAGE: done */}
          {stage === 'done' && result && (
            <>
              <div className="text-center py-2 mb-4">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                <p className="font-bold text-gray-900 text-lg">
                  {result.summary?.created || 0} added · {result.summary?.updated || 0} updated
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {result.summary?.skipped || 0} skipped · {result.summary?.failed || 0} failed · {result.summary?.total || 0} total
                </p>
              </div>
              {businessType === 'both' && (
                <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl p-3.5 mb-4">
                  {mode === 'sale' ? <Car className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" /> : <Tag className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />}
                  <p className="text-sm text-gray-700">
                    Also have {mode === 'sale' ? 'rental' : 'for-sale'} vehicles? Tap <span className="font-semibold">Import more</span> below and switch to <span className="font-semibold">{mode === 'sale' ? 'Rental vehicles' : 'Vehicles for sale'}</span>.
                  </p>
                </div>
              )}
              {Array.isArray(result.results) && result.results.some((r) => r.status !== 'created') && (
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-gray-50">
                        {result.results.filter((r) => r.status !== 'created').map((r, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2 text-gray-400">{r.row}</td>
                            <td className="px-3 py-2 font-mono text-xs font-semibold text-gray-800">{r.registrationNumber || '—'}</td>
                            <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                            <td className="px-3 py-2 text-xs text-gray-500">{r.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* footer */}
        <div className="p-5 border-t border-gray-100 flex items-center justify-end gap-3">
          {stage === 'preview' && (
            <>
              <button onClick={onClose} className="px-4 py-2.5 rounded-xl font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={doImport} disabled={!validRows.length || importing}
                className="px-5 py-2.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
                {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <>Import {validRows.length} vehicle{validRows.length !== 1 ? 's' : ''} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </>
          )}
          {stage === 'done' && (
            <>
              <button onClick={reset} className="px-4 py-2.5 rounded-xl font-semibold text-gray-600 hover:bg-gray-100 inline-flex items-center gap-1.5"><Upload className="w-4 h-4" /> Import more</button>
              <button onClick={onClose} className="px-5 py-2.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700">Done</button>
            </>
          )}
          {stage === 'pick' && (
            <button onClick={onClose} className="px-4 py-2.5 rounded-xl font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
          )}
        </div>
      </div>
    </div>
  );
}
